import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, Input, OnInit, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectUser } from '../../../../store/app.selectors';
import { TranslateService } from '@ngx-translate/core';

import { SnackBarService } from '../../../../core/services/snackbar.service';
import { AlertModalComponent } from '../../../shared/components/alert/alert.component';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { ImagesService } from '../../../shared/services/images.service';
import { IUserProfile } from '../../../user/interfaces/IUserProfile';
import { ChannelRoles } from '../../interfaces/IChannel';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';
import { MessagingApiService } from '../../services/messaging-api/messaging-api.service';
import { changeMemberCountInChannel } from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';
import { SelectUsersModalComponent } from '../select-users/select-users.component';

@Component({
  selector: 'ngx-members',
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MembersComponent extends BaseComponent implements OnInit {
  @Input() page = 0;
  @Input() text = '';
  @Input() isLoadingNewMembers: boolean;
  isLoadingAllMembers = false;
  @Input() useExternalMembers: boolean;
  @Input() canUseFilter = true;
  @Input() hideLoged: boolean;
  @Input() members: IMessageableEntity[] = [];
  @Input() membersSelected: IMessageableEntity[] = [];
  @Input() isLoadingAllCheck: boolean;
  @Input() showFilterInput = false;
  @Input() showChecks: boolean;
  @Input() showRoles: boolean;
  @Input() canDelete: boolean;
  @Input() canAddMembers: boolean;
  @Input() listHeight = '60vh';

  private dialog = inject(MatDialog);
  private dataService = inject(MessagingApiService);
  private store = inject(Store<AppStateWithChat>);
  private snackBarService = inject(SnackBarService);
  private translateService = inject(TranslateService);
  public imagesService = inject(ImagesService);

  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });
  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });
  selectedChannel = () => this.chat().selectedChannel;

  rols = [
    { name: 'ADMIN', value: ChannelRoles.ADMIN },
    { name: 'MEMBER', value: ChannelRoles.MEMBER },
    { name: 'VIEWER', value: ChannelRoles.VIEWER },
  ];

  private previousChannelUid: string | null = null;

  constructor() {
    super();
    effect(() => {
      const channel = this.chat().selectedChannel;
      if (channel?.uid !== this.previousChannelUid) {
        this.previousChannelUid = channel?.uid ?? null;
        if (!this.useExternalMembers) {
          this.page = 0;
          this.members = [];
          this.searchMembers();
        }
      }
    });

    effect((onCleanup) => {
      const content = this.membersSearch();
      if (!content) {
        return;
      }
      const subscription = fromEvent<KeyboardEvent>(content.nativeElement, 'keyup')
        .pipe(
          map((event: KeyboardEvent) => (event.target as HTMLInputElement).value),
          debounceTime(500),
          distinctUntilChanged()
        )
        .subscribe((searchText: string) => {
          this.page = 1;
          this.members = [];
          this.text = searchText;
          this.searchMembers();
        });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  membersSearch = viewChild<ElementRef<HTMLInputElement>>('membersSearch');

  ngOnInit(): void {
    // lifecycle hook kept for compatibility with parent init order
  }

  searchMembers() {
    this.isLoadingNewMembers = true;
    this.dataService.getMembersChannel(this.page, 100).subscribe(
      (data) => {
        this.isLoadingNewMembers = false;
        this.members = [...this.members, ...data];
        const user = this.user();
        if (this.hideLoged && user?.subjectMessageableEntity?.businessReference?.entityUID) {
          this.members = this.members.filter(
            (member) => member.businessReference?.entityUID !== user.subjectMessageableEntity!.businessReference!.entityUID
          );
        }
      },
      () => {
        this.isLoadingNewMembers = false;
        this.snackBarService.showErrorSnackBar('NGX-MESSAGING.LIST-MEMBERS.getMembersError');
      }
    );
  }

  deleteMember(member: IMessageableEntity): void {
    this.dialog
      .open(AlertModalComponent, {
        width: '300px',
        data: {
          title: this.translateService.instant('NGX-MESSAGING.LIST-MEMBERS.deleteMember') as string,
          message: this.translateService.instant('NGX-MESSAGING.LIST-MEMBERS.areYouSureToDeleteThisMember', {
            memberName: member.displayName,
          }) as string,
        },
      })
      .afterClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.isLoadingAllMembers = true;
          const selectedChannel = this.selectedChannel();
          this.dataService.deleteMemberChannel([member.uid]).subscribe(
            () => {
              this.store.dispatch(
                changeMemberCountInChannel({
                  channelId: selectedChannel!.uid,
                  value: (selectedChannel?.totalMembersCount ?? 0) - 1,
                })
              );
              this.members = this.members.filter((currentMember) => currentMember.uid !== member.uid);
              this.isLoadingAllMembers = false;
            },
            () => {
              this.isLoadingAllMembers = false;
              this.snackBarService.showErrorSnackBar('NGX-MESSAGING.LIST-MEMBERS.deleteMemberError');
            }
          );
        }
      });
  }

  addNewMembers(members: IMessageableEntity[]) {
    this.isLoadingAllMembers = true;
    const selectedChannel = this.selectedChannel();
    this.dataService.addMembersChannel(members.map((member) => member.uid)).subscribe(
      () => {
        this.store.dispatch(
          changeMemberCountInChannel({
            channelId: selectedChannel!.uid,
            value: (selectedChannel?.totalMembersCount ?? 0) + members.length,
          })
        );
        this.members = [...this.members, ...members.map((member) => ({ ...member, role: ChannelRoles.MEMBER }))];
        this.isLoadingAllMembers = false;
      },
      () => {
        this.isLoadingAllMembers = false;
        this.snackBarService.showErrorSnackBar('NGX-MESSAGING.LIST-MEMBERS.addMembersError');
      }
    );
  }

  changeRole(member: IMessageableEntity, rol: { name: string; value: ChannelRoles }) {
    this.isLoadingNewMembers = true;
    this.dataService.changeMemberRole(member.uid, rol.value).subscribe(
      () => {
        this.isLoadingNewMembers = false;
        member.role = rol.value;
      },
      () => {
        this.isLoadingNewMembers = false;
        this.snackBarService.showErrorSnackBar('NGX-MESSAGING.LIST-MEMBERS.changeRoleMembersError');
      }
    );
  }

  selectAllmembers(): void {
    if (!this.useExternalMembers) {
      this.isLoadingNewMembers = true;
      this.isLoadingAllCheck = true;
      this.dataService.getMembersChannel(1, 999).subscribe(
        (data) => {
          this.clearSelectedmembers();
          this.addAllmembersSelected(data);
          this.isLoadingNewMembers = false;
          this.isLoadingAllCheck = false;
        },
        () => {
          this.isLoadingNewMembers = false;
          this.isLoadingAllCheck = false;
        }
      );
    }
  }

  selectMember(user: IMessageableEntity) {
    const index = this.membersSelected.indexOf(user);
    if (index === -1) {
      this.membersSelected.push(user);
    } else {
      this.membersSelected.splice(index, 1);
    }
  }

  nextPage(): void {
    if (!this.useExternalMembers) {
      this.page++;
      this.searchMembers();
    }
  }

  isMemberSelected(user: IMessageableEntity): boolean {
    return this.membersSelected.some((e) => e.uid === user.uid);
  }

  selectAll(event: { checked: boolean }): void {
    if (event.checked) {
      this.selectAllmembers();
      if (this.useExternalMembers) {
        this.clearSelectedmembers();
        this.addAllmembersSelected(this.members);
      }
    } else {
      this.clearSelectedmembers();
    }
  }

  addAllmembersSelected(newmembers: IMessageableEntity[]): void {
    for (const user of newmembers) {
      this.membersSelected.push(user);
    }
  }

  clearSelectedmembers(): void {
    while (this.membersSelected.length) {
      this.membersSelected.pop();
    }
  }

  openAddModal(): void {
    this.dialog
      .open(SelectUsersModalComponent, {
        width: '500px',
        data: {
          usersDisabled: this.members,
        },
      })
      .afterClosed()
      .subscribe((data) => {
        if (data?.length) {
          this.addNewMembers(data as IMessageableEntity[]);
        }
      });
  }
}
